import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Save, Sparkles, Plus, Trash2, CalendarClock, GripVertical, CheckCircle2, Clock, Circle,
} from "lucide-react";

const groupLabel = (v) => (v && v.includes("|") ? v.split("|").join(" · ") : v || "");

const PERIODS = [
  { id: "semana", label: "Semanal" },
  { id: "quincena", label: "Quincenal" },
  { id: "mes", label: "Mensual" },
  { id: "bimestre", label: "Bimestral" },
];

const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-nardo-200 text-nardo-600", icon: Circle },
  en_curso: { label: "En curso", cls: "bg-amber-500 text-white", icon: Clock },
  completado: { label: "Completado", cls: "bg-emerald-500 text-white", icon: CheckCircle2 },
};
const nextStatus = (s) => (s === "pendiente" ? "en_curso" : s === "en_curso" ? "completado" : "pendiente");

export default function Dosificacion({ profile }) {
  const grades = useMemo(() => (profile?.all_grades || []).map((g) => g.grade), [profile]);
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [periodType, setPeriodType] = useState("bimestre");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { if (!grade && grades.length) setGrade(grades[0]); /* eslint-disable-next-line */ }, [grades]);

  const load = async () => {
    if (!grade) return;
    setLoading(true);
    try {
      const { data } = await api.get("/docente/dosificacion", { params: { grade } });
      setItems(data.items || []);
      setSubject(data.subject || "");
      setPeriodType(data.period_type || "bimestre");
      setDirty(false);
    } catch { toast.error("No se pudo cargar la dosificación"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [grade]);

  const setItem = (id, key, val) => { setItems((x) => x.map((it) => (it.id === id ? { ...it, [key]: val } : it))); setDirty(true); };
  const cycleStatus = (id) => { setItems((x) => x.map((it) => (it.id === id ? { ...it, status: nextStatus(it.status) } : it))); setDirty(true); };
  const addRow = () => { setItems((x) => [...x, { id: `dt_${Math.random().toString(36).slice(2, 10)}`, period: `Periodo ${x.length + 1}`, tema: "", contenido: "", pda: "", status: "pendiente" }]); setDirty(true); };
  const removeRow = (id) => { setItems((x) => x.filter((it) => it.id !== id)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/docente/dosificacion", { grade, period_type: periodType, items });
      setItems(data.items || items);
      setDirty(false);
      toast.success("Dosificación guardada");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const generate = async () => {
    if (items.length && !window.confirm("Esto reemplazará el temario actual con la propuesta de IA. ¿Continuar?")) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/docente/dosificacion/generate", { grade, period_type: periodType });
      setItems(data.items || []);
      setDirty(true);
      toast.success("Temario generado con IA · revísalo y guarda");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar"); }
    finally { setGenerating(false); }
  };

  const done = items.filter((i) => i.status === "completado").length;
  const progress = items.length ? Math.round((done / items.length) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="docente-dosificacion">
      <div className="doc-glass rounded-2xl p-5 border border-white/50 shadow-lg">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="dos-grade" className="doc-input min-w-[8rem]">
              {grades.length === 0 && <option value="">Sin grupos</option>}
              {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Materia</label>
            <div className="doc-input min-w-[9rem] bg-navy/5" data-testid="dos-subject">{subject || "—"}</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Periodicidad</label>
            <select value={periodType} onChange={(e) => { setPeriodType(e.target.value); setDirty(true); }} data-testid="dos-period" className="doc-input min-w-[8rem]">
              {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[8rem] flex justify-end gap-2">
            <button onClick={generate} disabled={generating || !grade} data-testid="dos-generate"
              className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? "Generando…" : "Generar con IA"}
            </button>
            <button onClick={save} disabled={saving || !grade || !dirty} data-testid="dos-save"
              className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
        {items.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] text-nardo-600 mb-1">
              <span>Avance del ciclo</span>
              <span className="font-semibold">{done}/{items.length} temas · {progress}%</span>
            </div>
            <div className="h-2 rounded-full bg-nardo-200 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} data-testid="dos-progress-bar" />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="doc-glass rounded-2xl border border-white/50 py-14 text-center text-nardo-500 text-sm">
          <CalendarClock className="w-10 h-10 mx-auto mb-3 text-nardo-300" />
          Aún no hay temario. Usa <b className="text-navy">Generar con IA</b> para una propuesta por grado y materia, o agrégalo tú.
          <div className="mt-4"><button onClick={addRow} data-testid="dos-add-empty" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 rounded-lg font-semibold transition-colors"><Plus className="w-4 h-4" /> Agregar periodo</button></div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="dos-list">
          {items.map((it, idx) => {
            const st = STATUS[it.status] || STATUS.pendiente;
            const StIcon = st.icon;
            return (
              <div key={it.id} data-testid={`dos-row-${it.id}`} className="doc-glass rounded-2xl border border-white/50 shadow p-4">
                <div className="flex items-start gap-3">
                  <div className="text-nardo-300 pt-1"><GripVertical className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <input value={it.period} onChange={(e) => setItem(it.id, "period", e.target.value)} data-testid={`dos-period-${it.id}`}
                        className="doc-input font-semibold text-navy w-32" placeholder="Periodo" />
                      <input value={it.tema} onChange={(e) => setItem(it.id, "tema", e.target.value)} data-testid={`dos-tema-${it.id}`}
                        className="doc-input flex-1 min-w-[10rem]" placeholder="Tema / proyecto" />
                      <button onClick={() => cycleStatus(it.id)} data-testid={`dos-status-${it.id}`}
                        className={`text-[10px] font-semibold px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 ${st.cls}`}><StIcon className="w-3 h-3" /> {st.label}</button>
                    </div>
                    <textarea value={it.contenido} onChange={(e) => setItem(it.id, "contenido", e.target.value)} data-testid={`dos-contenido-${it.id}`}
                      rows={2} className="doc-input w-full resize-none text-xs" placeholder="Contenidos y actividades" />
                    <textarea value={it.pda} onChange={(e) => setItem(it.id, "pda", e.target.value)} data-testid={`dos-pda-${it.id}`}
                      rows={1} className="doc-input w-full resize-none text-xs" placeholder="PDA (proceso de desarrollo de aprendizaje)" />
                  </div>
                  <button onClick={() => removeRow(it.id)} data-testid={`dos-del-${it.id}`} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
          <button onClick={addRow} data-testid="dos-add" className="w-full text-xs inline-flex items-center justify-center gap-1.5 border border-dashed border-navy/40 text-navy hover:bg-navy/5 px-4 py-3 rounded-xl font-semibold"><Plus className="w-4 h-4" /> Agregar periodo</button>
        </div>
      )}
    </div>
  );
}
